import type { FormlyFieldConfig } from '@ngx-formly/core';
import type { RevealState } from './reveal-menu.types';

/** Field types that render a textarea. `linked-value` is the stop note on the order forms. */
const TEXT_TYPES = new Set(['textarea', 'linked-value']);

/**
 * Text fields open in a panel with Annuleren/Opslaan; a group (note + photo flag) does too. A
 * repeat (`fieldArray`, e.g. volumes) never does: its fieldGroup is its rows, and rows added in a
 * panel cannot be restored by setValue on Annuleren.
 */
export function opensInPanel(field: FormlyFieldConfig): boolean {
  return TEXT_TYPES.has(String(field.type)) || (!!field.fieldGroup?.length && !field.fieldArray);
}

/**
 * Empty string, empty array, `false` and an object without any filled value all count as
 * "nothing" — an unticked flag (photo required: false) is a checkbox default, not content. Also the
 * string 'false': custom-toggle stores its flags as 'true'/'false'.
 */
export function isFilled(value: unknown): boolean {
  if (value === null || value === undefined || value === '' || value === false || value === 'false') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object' && !(value instanceof Date)) return Object.values(value as object).some(isFilled);
  return true;
}

/**
 * Where a revealable field starts, decided from its value before Formly's first hide pass — so an
 * edit view opens with its filled notes on screen (as chips), not behind the menu.
 */
export function initialRevealState(field: FormlyFieldConfig, value: unknown): RevealState {
  if (!isFilled(value)) return 'hidden';
  return opensInPanel(field) ? 'chip' : 'editing';
}

/** The chip's short value: the first text found, linked entries by their shown name. */
export function chipText(value: unknown, max = 40): string {
  const text = firstText(value)?.replace(/\s+/g, ' ').trim() ?? '';
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function firstText(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(firstText).filter(Boolean).join(', ') || null;
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const named = record['selectedInput'] ?? record['name'];
    if (typeof named === 'string' && named) return named;
    for (const nested of Object.values(record)) {
      // A flag beside the text (photo required) is not something to read out on a chip.
      if (typeof nested === 'boolean') continue;
      const text = firstText(nested);
      if (text) return text;
    }
  }
  return null;
}
